import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { formatPhoneNational } from "@/components/agents/setup/receptionist/phone-utils"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { availableNumbersAgentPath } from "@/routes"

// IMP-538: the setup wizards offer a short list of real, purchasable numbers
// (the requested area code first, then the closest ones nearby) and the user
// picks the one their agent gets. Launch buys exactly that number, so nobody
// is surprised by a number from the wrong area code.

export interface AvailableNumber {
  phone_number: string
  area_code: string
  locality: string | null
  region: string | null
  distance_miles: number | null
}

export type NumberChoices =
  // No complete area code: Launch assigns any available US number.
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; numbers: AvailableNumber[] }
  // Nothing in or near the area code.
  | { status: "empty" }
  // Search isn't set up on this server; Launch picks the closest number.
  | { status: "unavailable" }
  | { status: "error"; message: string }

// Fetches the choices for `areaCode`. Bump `refreshKey` to re-fetch (e.g.
// after the picked number was taken at Launch).
export function useNumberChoices(
  agentUid: string | undefined,
  areaCode: string,
  refreshKey = 0,
): { choices: NumberChoices; retry: () => void } {
  const [attempt, setAttempt] = useState(0)
  const searchable = agentUid != null && /^\d{3}$/.test(areaCode)
  // Each distinct search gets a key; a result only counts for the search that
  // produced it, so "loading" is simply "no result for the current key yet".
  const searchKey = `${areaCode}:${refreshKey}:${attempt}`
  const [result, setResult] = useState<{
    key: string
    choices: NumberChoices
  } | null>(null)

  useEffect(() => {
    if (!searchable) return
    const controller = new AbortController()
    const setChoices = (choices: NumberChoices) =>
      setResult({ key: searchKey, choices })
    fetch(
      // Option keys other than route params become the query string.
      availableNumbersAgentPath(agentUid, { area_code: areaCode }),
      {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
      },
    )
      .then(async (response) => {
        const body = (await response.json()) as {
          numbers?: AvailableNumber[]
          search_available?: boolean
          error?: string
        }
        if (!response.ok) {
          setChoices({
            status: "error",
            message: body.error ?? "Couldn't load available numbers.",
          })
        } else if (body.search_available === false) {
          setChoices({ status: "unavailable" })
        } else if (!body.numbers?.length) {
          setChoices({ status: "empty" })
        } else {
          setChoices({ status: "ready", numbers: body.numbers })
        }
      })
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return
        setChoices({
          status: "error",
          message: "Couldn't load available numbers.",
        })
      })
    return () => controller.abort()
  }, [agentUid, areaCode, searchable, searchKey])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  let choices: NumberChoices
  if (!searchable) choices = { status: "idle" }
  else if (result?.key !== searchKey) choices = { status: "loading" }
  else choices = result.choices
  return { choices, retry }
}

// Whether the picker lets Launch go ahead. With a list on screen the user
// must pick one; when there's nothing to pick from (no area code, or search
// unavailable/failed) Launch falls back to assigning the closest number.
export function numberChoiceComplete(
  choices: NumberChoices,
  selected: string,
): boolean {
  switch (choices.status) {
    case "ready":
      return selected !== ""
    case "loading":
    case "empty":
      return false
    default:
      return true
  }
}

export function PhoneNumberPicker({
  areaCode,
  choices,
  selected,
  onSelect,
  onRetry,
}: {
  areaCode: string
  choices: NumberChoices
  selected: string
  onSelect: (phoneNumber: string) => void
  onRetry: () => void
}) {
  if (choices.status === "idle") {
    return (
      <p className="text-muted-foreground text-xs">
        {areaCode === ""
          ? "Leave this blank and I’ll get any available US number."
          : "Enter a 3-digit area code to see available numbers."}
      </p>
    )
  }

  if (choices.status === "loading") {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Finding numbers in and near ({areaCode})…
      </p>
    )
  }

  if (choices.status === "empty") {
    return (
      <p className="text-destructive text-sm">
        No numbers are available in or near ({areaCode}) right now. Try a
        different area code.
      </p>
    )
  }

  if (choices.status === "unavailable") {
    return (
      <p className="text-muted-foreground text-xs">
        If ({areaCode}) isn&rsquo;t available, the closest match will be
        assigned when you launch.
      </p>
    )
  }

  if (choices.status === "error") {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-destructive">{choices.message}</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={onRetry}
        >
          Try again
        </Button>
        <span className="text-muted-foreground w-full text-xs">
          Or launch anyway and I&rsquo;ll get the closest available number.
        </span>
      </div>
    )
  }

  // A number picked on an earlier visit may not come back in a fresh search
  // (the list varies); keep it on screen so the choice stays visible.
  const numbers = choices.numbers.some((n) => n.phone_number === selected)
    ? choices.numbers
    : selected
      ? [
          {
            phone_number: selected,
            area_code: selected.slice(2, 5),
            locality: null,
            region: null,
            distance_miles: null,
          },
          ...choices.numbers,
        ]
      : choices.numbers
  const hasExact = choices.numbers.some((n) => n.area_code === areaCode)

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {hasExact
          ? "Pick the number your callers will see."
          : `No numbers are left in (${areaCode}). These are the closest available.`}
      </p>
      <div
        role="radiogroup"
        aria-label="Available phone numbers"
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {numbers.map((n) => (
          <NumberOption
            key={n.phone_number}
            number={n}
            nearby={n.area_code !== areaCode}
            selected={n.phone_number === selected}
            onSelect={() => onSelect(n.phone_number)}
          />
        ))}
      </div>
    </div>
  )
}

function NumberOption({
  number,
  nearby,
  selected,
  onSelect,
}: {
  number: AvailableNumber
  nearby: boolean
  selected: boolean
  onSelect: () => void
}) {
  const place = [number.locality, number.region].filter(Boolean).join(", ")
  const detail = [
    place,
    nearby && number.distance_miles != null
      ? `${number.distance_miles} mi away`
      : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "rounded-lg border px-4 py-3 text-left transition-colors",
        selected
          ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
          : "border-border hover:bg-muted/30",
      )}
    >
      <span
        className={cn(
          "block text-sm font-semibold tabular-nums",
          selected && "text-blue-600 dark:text-blue-400",
        )}
      >
        {formatPhoneNational(number.phone_number)}
      </span>
      {detail && (
        <span className="text-muted-foreground mt-0.5 block text-xs">
          {detail}
        </span>
      )}
    </button>
  )
}
